import React, { useState, useRef, useEffect } from 'react';
import {X, Send, Heart, MapPin, HelpCircle, Minimize2, Maximize2} from 'lucide-react';
import { Button } from './ui/Button';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  hemocenters?: any[];
}

export function ChatbotWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      role: 'assistant',
      content: '👋 Olá! Sou o **Doe**, seu assistente virtual.\n\nPosso ajudar você a:\n🩸 Encontrar hemocentros próximos\n❓ Responder dúvidas sobre doação\n💝 Entender o impacto da sua doação\n\nComo posso ajudar hoje?',
      timestamp: new Date(),
    },
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll para última mensagem
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Foco no input quando abrir
  useEffect(() => {
    if (isOpen && !isMinimized) {
      inputRef.current?.focus();
    }
  }, [isOpen, isMinimized]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: inputMessage.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      // Tentar obter localização do usuário
      let userLocation;
      try {
        const position = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
        });
        userLocation = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        };
      } catch {
        // Geolocalização não disponível/negada
      }

      const response = await fetch('https://api.lumi.new/v1/functions/p383653214795382784/chatbot-assistant', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: inputMessage.trim(),
          userLocation,
        }),
      });

      if (!response.ok) throw new Error('Failed to get response');

      const data = await response.json();

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: data.response,
        timestamp: new Date(),
        hemocenters: data.hemocenters,
      };

      setMessages(prev => [...prev, assistantMessage]);

    } catch (error) {
      console.error('Chatbot error:', error);
      
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: '😔 Desculpe, ocorreu um erro. Tente novamente em instantes ou entre em contato conosco.',
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickAction = (action: string) => {
    setInputMessage(action);
    setTimeout(() => handleSendMessage(), 100);
  };

  const formatMessage = (content: string) => {
    // Converte Markdown básico para HTML
    return content
      .split('\n')
      .map((line, i) => {
        // Negrito
        line = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        // Lista
        if (line.trim().startsWith('•') || line.trim().startsWith('-')) {
          return `<li key="${i}">${line.trim().substring(1).trim()}</li>`;
        }
        return `<p key="${i}">${line}</p>`;
      })
      .join('');
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 w-16 h-16 bg-gradient-to-br from-[#E53E3E] to-red-600 rounded-full shadow-2xl hover:shadow-3xl transition-all hover:scale-110 flex items-center justify-center group"
        aria-label="Abrir chat do Doe"
      >
        <Heart className="h-7 w-7 text-white fill-current animate-pulse" />
        <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white animate-pulse"></div>
      </button>
    );
  }

  return (
    <div
      className={`fixed bottom-6 right-6 z-50 bg-white rounded-3xl shadow-2xl transition-all duration-300 ${
        isMinimized ? 'w-80 h-16' : 'w-96 h-[600px]'
      } max-w-[calc(100vw-3rem)] max-h-[calc(100vh-3rem)]`}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gradient-to-r from-[#E53E3E] to-red-600 rounded-t-3xl">
        <div className="flex items-center space-x-3">
          <div className="relative">
            <Heart className="h-6 w-6 text-white fill-current" />
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-white"></div>
          </div>
          <div>
            <h3 className="text-white font-bold text-lg">Doe</h3>
            <p className="text-red-100 text-xs">Assistente Virtual</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            aria-label={isMinimized ? 'Maximizar' : 'Minimizar'}
          >
            {isMinimized ? (
              <Maximize2 className="h-4 w-4 text-white" />
            ) : (
              <Minimize2 className="h-4 w-4 text-white" />
            )}
          </button>
          <button
            onClick={() => setIsOpen(false)}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            aria-label="Fechar chat"
          >
            <X className="h-5 w-5 text-white" />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 h-[calc(100%-180px)]">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                    message.role === 'user'
                      ? 'bg-[#E53E3E] text-white'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  <div
                    className="text-sm leading-relaxed prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: formatMessage(message.content) }}
                  />
                  
                  {/* Hemocenters Cards */}
                  {message.hemocenters && message.hemocenters.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {message.hemocenters.slice(0, 3).map((hemo: any, idx: number) => (
                        <div key={idx} className="bg-white rounded-lg p-3 text-gray-800 border border-gray-200">
                          <h4 className="font-bold text-sm text-[#E53E3E]">{hemo.name}</h4>
                          <p className="text-xs text-gray-600 mt-1">
                            {hemo.street}, {hemo.number} - {hemo.city}/{hemo.state}
                          </p>
                          {hemo.phone && (
                            <p className="text-xs text-gray-600 mt-1">📞 {hemo.phone}</p>
                          )}
                          {hemo.distance && (
                            <p className="text-xs text-green-600 mt-1">
                              📍 {hemo.distance.toFixed(1)} km de você
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <p className="text-xs opacity-70 mt-2">
                    {message.timestamp.toLocaleTimeString('pt-BR', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </p>
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 rounded-2xl px-4 py-3">
                  <div className="flex space-x-2">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Actions */}
          <div className="px-4 py-2 border-t border-gray-100 flex gap-2 overflow-x-auto">
            <button
              onClick={() => handleQuickAction('Onde posso doar sangue?')}
              className="flex items-center space-x-1 px-3 py-2 bg-red-50 text-[#E53E3E] rounded-full text-xs font-medium hover:bg-red-100 transition-colors whitespace-nowrap"
            >
              <MapPin className="h-3 w-3" />
              <span>Hemocentros</span>
            </button>
            <button
              onClick={() => handleQuickAction('Quais os requisitos para doar?')}
              className="flex items-center space-x-1 px-3 py-2 bg-blue-50 text-blue-600 rounded-full text-xs font-medium hover:bg-blue-100 transition-colors whitespace-nowrap"
            >
              <HelpCircle className="h-3 w-3" />
              <span>Requisitos</span>
            </button>
            <button
              onClick={() => handleQuickAction('Como funciona a doação?')}
              className="flex items-center space-x-1 px-3 py-2 bg-green-50 text-green-600 rounded-full text-xs font-medium hover:bg-green-100 transition-colors whitespace-nowrap"
            >
              <Heart className="h-3 w-3" />
              <span>Processo</span>
            </button>
          </div>

          {/* Input Area */}
          <div className="p-4 border-t border-gray-100">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSendMessage();
              }}
              className="flex items-center space-x-2"
            >
              <input
                ref={inputRef}
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder="Digite sua mensagem..."
                disabled={isLoading}
                className="flex-1 px-4 py-3 border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-[#E53E3E] focus:border-transparent text-sm disabled:bg-gray-50 disabled:cursor-not-allowed"
              />
              <button
                type="submit"
                disabled={!inputMessage.trim() || isLoading}
                className="p-3 bg-[#E53E3E] text-white rounded-full hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Enviar mensagem"
              >
                <Send className="h-5 w-5" />
              </button>
            </form>
          </div>
        </>
      )}
    </div>
  );
}
